// Tiny SVG sparkline used by DashboardsDrawerV2 + KPI rows.
// Same algorithm as the design canvas reference.

import React, { useId } from 'react';
import { useTheme } from '@mui/material/styles';

export interface SparklineProps {
    data: number[];
    color?: string;
    width?: number;
    height?: number;
    fill?: boolean;
    strokeWidth?: number;
}

export const Sparkline: React.FC<SparklineProps> = ({
    data,
    color,
    width = 100,
    height = 28,
    fill = true,
    strokeWidth = 1.5,
}) => {
    const theme = useTheme();
    const resolvedColor = color ?? theme.palette.primary.main;
    const gradientId = useId();
    if (!data || data.length === 0) return null;

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const dx = data.length > 1 ? width / (data.length - 1) : width;

    const pts = data.map((v, i) => [
        i * dx,
        height - ((v - min) / range) * (height - 4) - 2,
    ]);

    const linePath = pts
        .map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1))
        .join(' ');
    const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

    return (
        <svg width={width} height={height} style={{ display: 'block' }} aria-hidden>
            {fill && (
                <>
                    <defs>
                        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor={resolvedColor} stopOpacity={0.25} />
                            <stop offset="100%" stopColor={resolvedColor} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <path d={areaPath} fill={`url(#${gradientId})`} />
                </>
            )}
            <path
                d={linePath}
                stroke={resolvedColor}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};
